import { CATALOG_REVIEW } from "@preedanext/ui/catalog-review";
// The storefront at the root of assets.preedanext.com (owner decision 29/09/2569). Vanilla TS —
// no React, no SSR at all here, so "engine loaded at runtime, never SSR" (packages/ui/DESIGN.md)
// holds trivially: the three.js model viewer is only ever pulled in by a dynamic import() when a
// 3D card is actually opened (see model-viewer.ts).

import { LANDINGS, landingDesignUrl } from "@preedanext/ui/site-registry";
import { BRANDS, brandLabel, resolveBrandId } from "./brands";
import { type Lang, readLang, t, writeLang } from "./i18n";
import {
  Copy,
  Download,
  ExternalLink,
  icon,
  KIND_ICON,
  Search,
  X,
} from "./icons";
import type { AssetKind, AssetManifest, ManifestItem } from "./manifest-types";
import type { ModelViewerHandle } from "./model-viewer";

// ---- state --------------------------------------------------------------------------------

interface State {
  manifest: AssetManifest | null;
  lang: Lang;
  query: string;
  brand: string | null;
  openItem: ManifestItem | null;
}

const state: State = {
  manifest: null,
  lang: readLang(),
  query: "",
  brand: null,
  openItem: null,
};

let activeViewer: ModelViewerHandle | null = null;

// ---- sections, in the order the owner specified ---------------------------------------------

const SECTIONS: {
  kind: AssetKind;
  titleKey:
    | "section_mark"
    | "section_image"
    | "section_video"
    | "section_model"
    | "section_document"
    | "section_audio";
}[] = [
  { kind: "mark", titleKey: "section_mark" },
  { kind: "image", titleKey: "section_image" },
  { kind: "video", titleKey: "section_video" },
  { kind: "model", titleKey: "section_model" },
  { kind: "document", titleKey: "section_document" },
  { kind: "audio", titleKey: "section_audio" },
];

// ---- utilities ------------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  for (const child of children) node.append(child);
  return node;
}

function formatBytes(n: number | null): string {
  if (n === null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function fileName(key: string): string {
  const parts = key.split("/");
  return parts[parts.length - 1] ?? key;
}

function itemMatchesQuery(item: ManifestItem, query: string): boolean {
  if (!query) return true;
  const haystack =
    `${item.key} ${item.names.join(" ")} ${(item.marks ?? []).map((mark) => Object.values(mark).join(" ")).join(" ")}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function itemMatchesBrand(item: ManifestItem, brand: string | null): boolean {
  if (!brand) return true;
  return resolveBrandId(item.brand) === brand;
}

function filteredItems(kind: AssetKind): ManifestItem[] {
  const items = state.manifest?.items ?? [];
  return items.filter(
    (item) =>
      item.kind === kind &&
      itemMatchesQuery(item, state.query) &&
      itemMatchesBrand(item, state.brand),
  );
}

// ---- rendering: shell -------------------------------------------------------------------------

function applyTheme() {
  // "auto" is the default: no data-theme attribute, so the page follows prefers-color-scheme,
  // exactly like packages/ui/src/styles/m3/preedanext.css's own :root / :root[data-theme] rules.
  document.documentElement.lang = state.lang;
}

function render() {
  applyTheme();
  const root = document.getElementById("app");
  if (!root) return;
  root.replaceChildren(renderHeader(), renderBody(), renderFooter());
  if (state.openItem) renderPreview(state.openItem);
  else closePreview();
}

function renderHeader(): HTMLElement {
  const manifest = state.manifest;
  const measuredLine = manifest
    ? manifest.measured
      ? `${t("measuredAt", state.lang)} ${new Date(manifest.generatedAt).toLocaleString(state.lang === "th" ? "th-TH" : "en-GB")}`
      : t("notMeasured", state.lang)
    : "";

  const searchInput = el("input", {
    class: "pnx-search-input",
    type: "search",
    placeholder: t("searchPlaceholder", state.lang),
    value: state.query,
    "aria-label": t("searchPlaceholder", state.lang),
  }) as HTMLInputElement;
  searchInput.addEventListener("input", () => {
    state.query = searchInput.value;
    render();
    searchInput.focus();
    searchInput.setSelectionRange(
      searchInput.value.length,
      searchInput.value.length,
    );
  });

  const brandSelect = el(
    "select",
    { class: "pnx-brand-select", "aria-label": t("filterBrand", state.lang) },
    el("option", { value: "" }, t("filterAll", state.lang)),
    ...BRANDS.map((b) =>
      el(
        "option",
        { value: b.id, ...(state.brand === b.id ? { selected: "true" } : {}) },
        b.label[state.lang],
      ),
    ),
  ) as HTMLSelectElement;
  brandSelect.value = state.brand ?? "";
  brandSelect.addEventListener("change", () => {
    state.brand = brandSelect.value || null;
    render();
  });

  const langButton = el(
    "button",
    { class: "pnx-chip-btn", type: "button", "aria-label": "Language" },
    state.lang === "th" ? "TH" : "EN",
  );
  langButton.addEventListener("click", () => {
    state.lang = state.lang === "th" ? "en" : "th";
    writeLang(state.lang);
    render();
  });

  const themeButton = el(
    "button",
    { class: "pnx-chip-btn", type: "button", "aria-label": "Theme" },
    "☾/☀",
  );
  themeButton.addEventListener("click", () => {
    // Cycle system → light → dark → system. Starting the cycle at "light" (rather than picking
    // the opposite of the system preference) means the first click always visibly changes
    // something, even when the system preference already happens to match the next step.
    const current = document.documentElement.dataset.theme;
    const next =
      current === undefined
        ? "light"
        : current === "light"
          ? "dark"
          : undefined;
    if (next) document.documentElement.dataset.theme = next;
    else delete document.documentElement.dataset.theme;
  });

  const searchWrap = el(
    "div",
    { class: "pnx-search-wrap" },
    icon(Search, "pnx-icon pnx-icon-sm"),
    searchInput,
  );

  return el(
    "header",
    { class: "pnx-glass pnx-header" },
    el(
      "div",
      { class: "pnx-header-row" },
      el("div", { class: "pnx-brandmark" }, "PREEDA NEXT · assets"),
      el(
        "div",
        { class: "pnx-header-controls" },
        searchWrap,
        brandSelect,
        langButton,
        themeButton,
      ),
    ),
    el("p", { class: "pnx-measured-line" }, measuredLine),
  );
}

function renderBody(): HTMLElement {
  const manifest = state.manifest;
  if (!manifest) {
    return el(
      "main",
      { class: "pnx-main" },
      el("p", { class: "pnx-loading" }, "…"),
    );
  }
  const main = el("main", { class: "pnx-main" });
  main.append(
    el(
      "div",
      { class: "pnx-hero" },
      el("h1", {}, t("title", state.lang)),
      el("p", {}, t("subtitle", state.lang)),
    ),
  );
  main.append(renderSites());
  main.append(renderCatalogReview());
  for (const section of SECTIONS) {
    main.append(renderSection(section.kind, t(section.titleKey, state.lang)));
  }
  return main;
}

function renderCatalogReview(): HTMLElement {
  const lang = state.lang;
  const section = el(
    "section",
    { class: "pnx-section", id: "catalog-review" },
    el(
      "h2",
      {},
      lang === "th"
        ? "แบบแคตตาล็อกก่อนย้ายเว็บ"
        : "Catalog design before migration",
    ),
    el(
      "p",
      {},
      lang === "th"
        ? "โครงหน้าจอสำหรับตรวจหมวดและข้อมูลที่ต้องแสดง ยังไม่ใช่ทะเบียนสินค้าหรือราคาจริง และยังไม่เปลี่ยนเว็บ SPMS–EST"
        : "Screen structure for reviewing categories and display fields. These are not live product or price records, and SPMS–EST has not been switched over.",
    ),
    el(
      "a",
      {
        href: "https://www.figma.com/design/Vlfim8sphO761cgu6yncSh",
        class: "pnx-link",
      },
      lang === "th" ? "เปิดแบบใน Figma" : "Open Figma designs",
    ),
  );
  const grid = el("div", { class: "pnx-review-grid" });
  for (const item of CATALOG_REVIEW) {
    const card = el(
      "article",
      { class: "pnx-review-card", id: `review-${item.id}` },
      el("h3", {}, item.title[lang]),
      el("p", {}, item.description[lang]),
    );
    card.append(
      el("h4", {}, lang === "th" ? "หมวดที่ต้องแยก" : "Category groups"),
    );
    card.append(
      el("ul", {}, ...item.groups.map((group) => el("li", {}, group[lang]))),
    );
    const details = el(
      "details",
      {},
      el(
        "summary",
        {},
        lang === "th" ? "ข้อมูลและหน่วยที่ต้องเห็น" : "Fields and units to display",
      ),
    );
    details.append(
      el("ul", {}, ...item.fields.map((field) => el("li", {}, field[lang]))),
    );
    card.append(details);
    grid.append(card);
  }
  section.append(grid);
  return section;
}

function renderSites(): HTMLElement {
  const th = state.lang === "th";
  const section = el(
    "section",
    { class: "pnx-section", id: "sites" },
    el("h2", {}, th ? "เว็บและแบบต้นฉบับ" : "Websites and source designs"),
    el(
      "p",
      {},
      th
        ? "เปิดเว็บจริงหรือแบบ Figma ที่ใช้พัฒนาของแต่ละบริษัท"
        : "Open each company's website or its Figma design.",
    ),
  );
  const list = el("ul", { class: "pnx-site-list" });
  for (const landing of Object.values(LANDINGS)) {
    const links = el("div", { class: "pnx-site-links" });
    for (const [label, href] of [
      [th ? "เปิดเว็บ" : "Website", `https://${landing.site}`],
      [th ? "แบบ Figma" : "Figma design", landingDesignUrl(landing)],
    ] as const)
      links.append(
        el("a", { href, target: "_blank", rel: "noopener noreferrer" }, label),
      );
    list.append(el("li", {}, el("h3", {}, landing.site), links));
  }
  section.append(
    list,
    el(
      "a",
      {
        href: "https://workspace.preedanext.com/office/integrations",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      th
        ? "เปิดไฟล์และบริการที่เชื่อมไว้ใน hub"
        : "Open connected files and services in hub",
    ),
  );
  return section;
}

function renderSection(kind: AssetKind, title: string): HTMLElement {
  const items = filteredItems(kind);
  const section = el(
    "section",
    { class: "pnx-section", id: `section-${kind}` },
    el(
      "div",
      { class: "pnx-section-head" },
      icon(KIND_ICON[kind], "pnx-icon pnx-icon-chip"),
      el("h2", {}, title),
      el("span", { class: "pnx-count" }, String(items.length)),
    ),
  );

  if (kind === "mark")
    section.append(renderColourStrip(), renderFontSpecimens());

  if (items.length === 0) {
    section.append(el("p", { class: "pnx-empty" }, t("empty", state.lang)));
    return section;
  }

  const grid = el("ul", { class: "pnx-grid", "aria-label": title });
  for (const item of items) grid.append(renderCard(item));
  section.append(grid);
  return section;
}

function renderColourStrip(): HTMLElement {
  return el(
    "div",
    { class: "pnx-colour-strip" },
    el("h3", { class: "pnx-subhead" }, t("colorsHeading", state.lang)),
    el(
      "ul",
      { class: "pnx-swatch-row" },
      ...BRANDS.map((b) =>
        el(
          "li",
          { class: "pnx-swatch" },
          el("span", {
            class: "pnx-swatch-chip",
            style: `background:${b.seed}`,
          }),
          el(
            "span",
            { class: "pnx-swatch-label" },
            b.label[state.lang],
            el("code", {}, b.seed),
          ),
        ),
      ),
    ),
  );
}

function renderFontSpecimens(): HTMLElement {
  const fonts = state.manifest?.items.filter((i) => i.kind === "font") ?? [];
  const wrap = el(
    "div",
    { class: "pnx-font-strip" },
    el("h3", { class: "pnx-subhead" }, t("fontsHeading", state.lang)),
  );
  if (fonts.length === 0) {
    wrap.append(el("p", { class: "pnx-empty" }, t("empty", state.lang)));
    return wrap;
  }
  for (const item of fonts) {
    const sample = el(
      "p",
      { class: "pnx-font-sample" },
      "Aa Bb Cc 0123456789 · สวัสดี ฟิล์มกรองแสง",
    );
    wrap.append(
      el(
        "div",
        { class: "pnx-font-card" },
        el("p", { class: "pnx-font-name" }, fileName(item.key)),
        sample,
      ),
    );
    loadSpecimenFont(item.url, sample);
  }
  return wrap;
}

function loadSpecimenFont(url: string, target: HTMLElement) {
  const family = `pnx-specimen-${Math.abs(hashOf(url))}`;
  const face = new FontFace(family, `url("${url}")`);
  face
    .load()
    .then(() => {
      document.fonts.add(face);
      target.style.fontFamily = family;
    })
    .catch(() => {
      target.textContent = t("fontFailed", state.lang);
    });
}

function hashOf(text: string): number {
  let h = 0;
  for (let i = 0; i < text.length; i += 1)
    h = (h * 31 + text.charCodeAt(i)) | 0;
  return h;
}

function renderCard(item: ManifestItem): HTMLElement {
  const thumb = el("div", { class: "pnx-thumb" });
  if (item.kind === "mark" || item.kind === "image") {
    const img = el("img", {
      src: item.url,
      alt: "",
      loading: "lazy",
    }) as HTMLImageElement;
    img.addEventListener("error", () => {
      img.replaceWith(icon(KIND_ICON[item.kind]));
    });
    thumb.append(img);
  } else {
    thumb.append(icon(KIND_ICON[item.kind], "pnx-icon pnx-icon-lg"));
  }

  const card = el(
    "li",
    { class: "pnx-card" },
    el(
      "button",
      { class: "pnx-card-btn", type: "button" },
      thumb,
      el(
        "span",
        { class: "pnx-card-meta" },
        el(
          "span",
          { class: "pnx-card-name" },
          item.marks?.length
            ? item.marks
                .map(
                  (mark) =>
                    `${mark.number} · ${brandLabel(mark.company, state.lang)} · ${mark.form} · ${mark.tone}`,
                )
                .join(" / ")
            : (item.names[0] ?? fileName(item.key)),
        ),
        el(
          "span",
          { class: "pnx-card-sub" },
          `${brandLabel(item.brand, state.lang)} · ${formatBytes(item.bytes)}`,
        ),
      ),
    ),
  );
  card.querySelector("button")?.addEventListener("click", () => {
    state.openItem = item;
    renderPreview(item);
  });
  return card;
}

function renderFooter(): HTMLElement {
  return el(
    "footer",
    { class: "pnx-footer" },
    el(
      "p",
      {},
      `assets.preedanext.com · ${state.manifest?.total ?? 0} ${t("itemsCount", state.lang)}`,
    ),
  );
}

// ---- preview panel --------------------------------------------------------------------------

function closePreview() {
  document.getElementById("pnx-preview")?.remove();
  document.removeEventListener("keydown", onPreviewKeydown);
  if (activeViewer) {
    activeViewer.dispose();
    activeViewer = null;
  }
}

function onPreviewKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    state.openItem = null;
    closePreview();
  }
}

function copyToClipboard(button: HTMLButtonElement, value: string) {
  navigator.clipboard
    .writeText(value)
    .then(() => {
      const original = button.textContent;
      button.textContent = t("copied", state.lang);
      setTimeout(() => {
        button.textContent = original;
      }, 1500);
    })
    .catch(() => {
      /* clipboard permission denied — the URL is still visible and selectable */
    });
}

function renderPreview(item: ManifestItem) {
  closePreview();
  const body = el("div", { class: "pnx-preview-body" });
  fillPreviewBody(body, item);

  const copyBtn = el(
    "button",
    { class: "pnx-btn", type: "button" },
    icon(Copy, "pnx-icon pnx-icon-sm"),
    t("copyUrl", state.lang),
  );
  copyBtn.addEventListener("click", () =>
    copyToClipboard(copyBtn as HTMLButtonElement, item.url),
  );

  const overlay = el(
    "div",
    {
      id: "pnx-preview",
      class: "pnx-overlay",
      role: "dialog",
      "aria-modal": "true",
    },
    el(
      "div",
      { class: "pnx-glass pnx-preview-panel" },
      el(
        "div",
        { class: "pnx-preview-head" },
        el(
          "h3",
          {},
          item.marks?.length
            ? item.marks
                .map(
                  (mark) =>
                    `${mark.number} · ${brandLabel(mark.company, state.lang)} · ${mark.form} · ${mark.tone}`,
                )
                .join(" / ")
            : (item.names[0] ?? fileName(item.key)),
        ),
        el(
          "button",
          {
            class: "pnx-icon-btn",
            type: "button",
            "aria-label": t("close", state.lang),
          },
          icon(X),
        ),
      ),
      body,
      el(
        "div",
        { class: "pnx-preview-actions" },
        copyBtn,
        el(
          "a",
          { class: "pnx-btn", href: item.url, download: "" },
          icon(Download, "pnx-icon pnx-icon-sm"),
          t("download", state.lang),
        ),
        el(
          "a",
          {
            class: "pnx-btn",
            href: item.url,
            target: "_blank",
            rel: "noopener noreferrer",
          },
          icon(ExternalLink, "pnx-icon pnx-icon-sm"),
          t("open", state.lang),
        ),
      ),
      item.credit
        ? el(
            "dl",
            { class: "pnx-credit" },
            el("dt", {}, t("credit", state.lang)),
            el(
              "dd",
              {},
              el(
                "a",
                {
                  href: item.credit.sourceUrl,
                  target: "_blank",
                  rel: "noopener noreferrer",
                },
                item.credit.author,
              ),
            ),
            el("dt", {}, t("licence", state.lang)),
            el(
              "dd",
              {},
              el(
                "a",
                {
                  href: item.credit.licenceUrl,
                  target: "_blank",
                  rel: "noopener noreferrer",
                },
                item.credit.licenceLabel,
              ),
            ),
          )
        : "",
    ),
  );
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      state.openItem = null;
      closePreview();
    }
  });
  overlay.querySelector(".pnx-icon-btn")?.addEventListener("click", () => {
    state.openItem = null;
    closePreview();
  });
  document.addEventListener("keydown", onPreviewKeydown);
  document.body.append(overlay);
  (overlay.querySelector(".pnx-icon-btn") as HTMLElement | null)?.focus();
}

// Preview grounds (checker · light · dark) live only in this page's own CSS via `data-ground` on
// `.pnx-preview-ground` — never baked into a file. The owner's rule (29/09/2569): a mark or logo
// must stay transparent; the checker/light/dark swap below is how a viewer checks that for
// themselves, the same way the hub's own asset detail panel does it.
const GROUND_LABEL: Record<
  "checker" | "light" | "dark",
  { th: string; en: string }
> = {
  checker: { th: "ตาราง", en: "Checker" },
  light: { th: "สว่าง", en: "Light" },
  dark: { th: "มืด", en: "Dark" },
};

function fillPreviewBody(body: HTMLElement, item: ManifestItem) {
  switch (item.kind) {
    case "mark":
    case "image": {
      const ground = el(
        "div",
        { class: "pnx-preview-ground", "data-ground": "checker" },
        el("img", { src: item.url, alt: "", class: "pnx-preview-image" }),
      );
      const toggle = el("div", {
        class: "pnx-ground-toggle",
        role: "group",
        "aria-label": "Background",
      });
      for (const g of ["checker", "light", "dark"] as const) {
        const btn = el(
          "button",
          {
            class: "pnx-chip-btn",
            type: "button",
            "aria-pressed": g === "checker" ? "true" : "false",
          },
          GROUND_LABEL[g][state.lang],
        );
        btn.addEventListener("click", () => {
          ground.dataset.ground = g;
          for (const other of Array.from(toggle.querySelectorAll("button"))) {
            other.setAttribute("aria-pressed", String(other === btn));
          }
        });
        toggle.append(btn);
      }
      body.append(el("div", { class: "pnx-preview-stack" }, ground, toggle));
      return;
    }
    case "video": {
      body.append(
        el("video", {
          src: item.url,
          controls: "true",
          preload: "metadata",
          class: "pnx-preview-video",
        }),
      );
      return;
    }
    case "document": {
      body.append(
        el("iframe", {
          src: item.url,
          class: "pnx-preview-pdf",
          title: fileName(item.key),
        }),
      );
      return;
    }
    case "audio": {
      body.append(
        el("audio", {
          src: item.url,
          controls: "true",
          class: "pnx-preview-audio",
        }),
      );
      return;
    }
    case "font": {
      const sample = el(
        "p",
        { class: "pnx-preview-font-sample" },
        "Aa Bb Cc Dd 0123456789 · สวัสดี ฟิล์มกรองแสง ๑๒๓",
      );
      body.append(sample);
      const loading = el(
        "p",
        { class: "pnx-empty" },
        t("loadingFont", state.lang),
      );
      body.append(loading);
      loadSpecimenFont(item.url, sample);
      setTimeout(() => loading.remove(), 50);
      return;
    }
    case "model": {
      const canvas = el("canvas", {
        class: "pnx-preview-canvas",
      }) as HTMLCanvasElement;
      const status = el(
        "p",
        { class: "pnx-model-status" },
        t("loadingModel", state.lang),
      );
      body.append(canvas, status);
      import("./model-viewer").then(({ mountModelViewer }) => {
        mountModelViewer(canvas, item.url, (s) => {
          if (s === "ready") status.remove();
          else if (s === "failed")
            status.textContent = t("modelFailed", state.lang);
        }).then((handle) => {
          activeViewer = handle;
        });
      });
      return;
    }
    default: {
      body.append(el("p", { class: "pnx-empty" }, fileName(item.key)));
    }
  }
}

// ---- boot -----------------------------------------------------------------------------------

async function boot() {
  render();
  try {
    const res = await fetch("/manifest.json", {
      headers: { accept: "application/json" },
    });
    if (!res.ok) throw new Error(`manifest answered ${res.status}`);
    state.manifest = (await res.json()) as AssetManifest;
  } catch {
    state.manifest = {
      generatedAt: new Date().toISOString(),
      bucket: "preedanext-public",
      measured: false,
      measuredReason: "manifest.json did not load",
      counts: {},
      total: 0,
      items: [],
    };
  }
  render();
  if (location.hash === "#catalog-review") {
    document.getElementById("catalog-review")?.scrollIntoView();
  }
}

// gsap (and gsap.ticker, the one animation loop this page ever runs — packages/ui/DESIGN.md) is
// only pulled in by the dynamic import() inside model-viewer.ts once a 3D preview actually opens;
// nothing at this top level imports it eagerly.
boot();
